import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Box,
} from "@mui/material";
import {
  dialogStyle,
  dialogContentStyle,
  footerStyle,
  dialogBox,
} from "./styles/styles";
import ViewJobHeader from "./ModalContent/ViewJobHeader";
import OrderDetails from "./ModalContent/OrderDetails";
import EditOrder from "./ModalContent/EditOrder";
import ClientCommunityDetails from "./ModalContent/ClientCommunityDetails";
import ProductOrderDetails from "./ModalContent/ProductOrderDetails";
import ProductClientCommunityDetails from "./ModalContent/ProductClientCommunityDetails";
import ModalActions from "./ModalContent/ModalActions";
import { useOrderMinimum, isBelowOrderMinimum } from "../../Constants/orderMinimum";
import CustomTabs from "../../Components/CustomTabs";
import { useTranslation } from "react-i18next";
import {
  PRODUCT_STATUS_LABEL_KEY,
  getNextProductStatusName,
} from "../../Constants/productOrderStatus";
import useProductStatusMap from "../../hooks/useProductStatusMap";

// const details = {
//   request_id: "1",
//   services: [
//     {
//       id: "1",
//       name: "שירות לדוגמה #1",
//       priced_variants: 0,
//       total_variants: 2,
//       variants: [
//         {
//           id: "1",
//           sku: 'מק"ט #1 לדוגמה',
//           amount: 1,
//           unit: "פריט",
//           price: "172",
//         },
//         {
//           id: "2",
//           sku: 'מק"ט #2 לדוגמה',
//           amount: 25,
//           unit: "מטר",
//           price: "289",
//         },
//       ],
//     },
//     {
//       id: "2",
//       name: "שירות לדוגמה #2",
//       priced_variants: 0,
//       total_variants: 2,
//       variants: [
//         {
//           id: "1",
//           sku: "צבע לבן",
//           amount: 1,
//           unit: "פריט",
//           price: "17",
//         },
//         {
//           id: "2",
//           sku: "צבע כחול",
//           amount: 25,
//           unit: "מטר",
//           price: null,
//         },
//       ],
//     },
//   ],
//   additional_info: {
//     images: [
//       "/images/vaccum-cleaning-image.png",
//       "/images/vaccum-cleaning-image.png",
//     ],
//     text: "מידע שהוקלד בזמן יצירת ההזמנה יופיע כאן",
//   },
//   client_name: "שם לקוח לדוגמה",
//   client_logo: "/images/avatar.png",
//   community_name: "קהילה לדוגמה",
//   community_logo: "/images/dummy-community-images/countryside-image.png",
//   client_email: "john.doe@example.com",
//   client_phone: "1234567890",
//   client_whatsapp: "1234567890",
//   created_at: "15/12/2025",
//   due_date: "31/12/2025",
//   completed_on: "01/05/2025",
//   location: {
//     client_address: "כתובת הלקוח לדוגמה, קומה 3",
//     online: false,
//     business_address: null,
//   },
//   time_of_day: ["אחר צהריים (14:00-17:00)"],
//   visit_price: "50",
//   total_price: "1",
//   community_discount_percentage: "97",
//   communitme_fee: "496",
// };

const ViewJobModal = ({
  viewType = "pending",
  openViewJobModal,
  jobData,
  handleCloseViewJobModal,
  handleDeclineButtonClick,
  handleApproveJob,
  actionButtonLoader,
  handleCancelButtonClick,
  handleRequestPayment,
  disabledRequestPaymentBtn,
  handleReturnProductForReapproval,
  returnJobLoader,
  handleAdvanceProductStatus,
  advanceStatusLoader,
  editMode,
  setEditMode,
  handleUpdateOrder,
  updateOrderLoader,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation("view-job-modal");
  const { t: tStatus } = useTranslation("page-my-jobs", {
    keyPrefix: "table_data",
  });

  // Product orders (COMY1-283) render product-specific panels; everything else
  // is the existing service/visit layout.
  // Store and restaurant orders are catalog orders: same details payload, same
  // panels, same status pipeline — only the tables behind them differ. Treating
  // a restaurant job as a service here renders the service panels, which then
  // crash on fields the catalog payload doesn't carry.
  const isProduct =
    jobData?.type === "product" || jobData?.type === "restaurant";
  const LeftPanel = isProduct ? ProductOrderDetails : OrderDetails;
  const RightPanel = isProduct
    ? ProductClientCommunityDetails
    : ClientCommunityDetails;

  // A product order the supplier already returned, now waiting on the member.
  const awaitingMember = isProduct && jobData?.awaiting_member;
  // On a PENDING product order the supplier says what they can actually
  // fulfil: un-check items they don't have, and cut a line down to the
  // quantity they do have (2 bars of chocolate ordered, 1 in stock). Once
  // anything is cut, the primary action becomes "Return to member for
  // re-approval". This stays editable even after a return (awaiting-member) so
  // the supplier can keep updating and re-submit. A single-item order counts
  // too — its quantity can be cut even though its row can't be the one that
  // empties the order.
  const showAvailability = isProduct && viewType === "pending";
  const [unavailableIds, setUnavailableIds] = useState([]);
  // Raised by the service-order editor while its edit would leave the order
  // under the payment minimum, so the footer's save button can refuse too.
  const [editBelowMinimum, setEditBelowMinimum] = useState(false);
  // itemId → the quantity the supplier can supply. An item absent from the map
  // is unchanged; the DB column behind it arrives with the backend half.
  const [quantities, setQuantities] = useState({});
  useEffect(() => {
    if (!openViewJobModal) return;
    // (Re)seed from the backend EVERY time the modal opens: a returned order
    // opens with the items the supplier already marked unavailable un-checked,
    // and any un-checks the supplier didn't submit (closed without "Return")
    // are discarded on reopen rather than silently kept.
    setUnavailableIds(
      (jobData?.ordered_products || [])
        .filter((it) => it.is_available === false)
        .map((it) => it.id),
    );
    setQuantities(
      Object.fromEntries(
        (jobData?.ordered_products || [])
          .filter((it) => it.available_quantity != null)
          .map((it) => [it.id, it.available_quantity]),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openViewJobModal, jobData?.job_id]);
  // Un-checking a row means none of it, so it also drops the row's quantity
  // back to the full ordered amount — re-checking then starts from a clean
  // line rather than from whatever was left before it was un-checked.
  const toggleItemAvailable = (itemId) =>
    setUnavailableIds((prev) => {
      if (prev.includes(itemId)) {
        setQuantities((q) => {
          const next = { ...q };
          delete next[itemId];
          return next;
        });
        return prev.filter((id) => id !== itemId);
      }
      return [...prev, itemId];
    });

  // The stepper and the checkbox are two views of one decision: cutting a line
  // to zero is un-checking it, raising it off zero checks it back on.
  const setItemQuantity = (itemId, quantity) => {
    const item = (jobData?.ordered_products || []).find((it) => it.id === itemId);
    const ordered = item?.quantity ?? 0;
    const next = Math.max(0, Math.min(quantity, ordered));
    if (next === 0) {
      setUnavailableIds((prev) =>
        prev.includes(itemId) ? prev : [...prev, itemId],
      );
      return;
    }
    setUnavailableIds((prev) => prev.filter((id) => id !== itemId));
    setQuantities((prev) => {
      const updated = { ...prev };
      if (next === ordered) delete updated[itemId];
      else updated[itemId] = next;
      return updated;
    });
  };

  // Returning a partial order only makes sense when SOME of it survives — if
  // the supplier empties every line they must cancel the whole order instead
  // of returning it.
  const orderedCount = jobData?.ordered_products?.length || 0;
  const someUnavailable =
    showAvailability &&
    (unavailableIds.length > 0 || Object.keys(quantities).length > 0);

  // …and what is left has to be worth charging for. The member cannot pay
  // below the house minimum, so an order cut under it is not a revision — it
  // is a cancellation, and the supplier is told that here rather than the
  // member discovering it on an order they are not allowed to approve.
  // Counted on what the member would actually pay: a reduced quantity bills
  // its own share of the line, an un-checked item bills nothing.
  const orderMinimum = useOrderMinimum();
  const remainingMemberValue = (jobData?.ordered_products || [])
    .filter((item) => !unavailableIds.includes(item.id))
    .reduce((sum, item) => {
      const ordered = Number(item.quantity) || 0;
      const approved = quantities[item.id] ?? ordered;
      const lineTotal = Number(item.subtotal) || 0;
      return sum + (ordered > 0 ? (lineTotal / ordered) * approved : lineTotal);
    }, 0);
  const belowMinimumAfterEdit =
    someUnavailable && isBelowOrderMinimum(remainingMemberValue, orderMinimum);

  // The same rule on the order as it stands: a job whose value never reached
  // the minimum cannot be sent for payment either — the member would be asked
  // for something the payment screen will refuse.
  const currentMemberValue =
    (Number(jobData?.order_summary?.price) || 0) -
    (Number(jobData?.order_summary?.community_discount) || 0);
  const belowMinimumOrder = isBelowOrderMinimum(
    currentMemberValue,
    orderMinimum,
  );

  const canReturn =
    someUnavailable &&
    unavailableIds.length < orderedCount &&
    !belowMinimumAfterEdit;

  // COMY1-292 — "Edit Order" is offered only on ACCEPTED (active) service
  // orders. A pending job hasn't been taken yet — the supplier must approve it
  // first (the backend guard likewise only allows statuses 20/25/30).
  const EDIT_ORDER_FORM_ID = "edit-order-form";
  const canEditOrder = !isProduct && viewType === "active";

  const leftPanelProps = isProduct
    ? {
        jobData,
        viewType,
        showAvailability,
        unavailableIds,
        onToggleItem: toggleItemAvailable,
        quantities,
        onQuantityChange: setItemQuantity,
      }
    : {
        jobData,
        viewType,
        onEditOrder: canEditOrder ? () => setEditMode(true) : undefined,
      };

  // Active product orders: the footer's primary action advances the order to its
  // next status (Confirmed → Preparing → Out for delivery / Ready → Completed)
  // instead of "Request Payment" (products are already paid). COMY1-283.
  // Worked out in status NAMES; the id to send is looked up in the map the
  // backend serves, so no status id is written in the client (COMY1-283).
  const { idByName: productStatusIdByName } = useProductStatusMap(jobData?.type);
  const nextProductStatusName = isProduct
    ? getNextProductStatusName(
        jobData?.product_status?.status,
        jobData?.fulfillment?.type === "pickup",
      )
    : null;
  const nextProductStatusId = nextProductStatusName
    ? productStatusIdByName[nextProductStatusName] ?? null
    : null;
  const nextProductStatusLabel = nextProductStatusName
    ? tStatus(PRODUCT_STATUS_LABEL_KEY[nextProductStatusName])
    : null;

  const tabs = [
    {
      label: t("order_content"),
      content: <LeftPanel {...leftPanelProps} />,
    },
    {
      label: t("order_details"),
      content: <RightPanel {...{ jobData, viewType }} />,
    },
  ];

  return (
    <>
      <Dialog
        fullScreen={isSmallScreen}
        open={openViewJobModal}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleCloseViewJobModal();
        }}
        disableEscapeKeyDown
        fullWidth
        maxWidth="1200px"
        sx={dialogStyle}
      >
        <Box sx={dialogBox}>
          <ViewJobHeader
            {...{
              jobData,
              viewType,
              onClose: handleCloseViewJobModal,
            }}
          />
          <DialogContent sx={dialogContentStyle}>
            {editMode ? (
              // Edit mode: the editor replaces the LEFT (Services & Variants)
              // panel while the RIGHT panel (Client & Community + Time &
              // Location) stays visible, per Figma. The Order Summary is hidden
              // during editing (prices are being changed). On mobile the editor
              // takes the full width.
              isMobile ? (
                <EditOrder
                  jobData={jobData}
                  formId={EDIT_ORDER_FORM_ID}
                  onSubmit={handleUpdateOrder}
                  onBack={() => setEditMode(false)}
                  onBelowMinimumChange={setEditBelowMinimum}
                />
              ) : (
                <>
                  <EditOrder
                    jobData={jobData}
                    formId={EDIT_ORDER_FORM_ID}
                    onSubmit={handleUpdateOrder}
                    onBack={() => setEditMode(false)}
                    onBelowMinimumChange={setEditBelowMinimum}
                  />
                  <RightPanel {...{ jobData, viewType, editMode: true }} />
                </>
              )
            ) : isMobile ? (
              <CustomTabs
                tabs={tabs}
                defaultTab={0}
                variant="fullWidth"
                onChange={(newValue) =>
                  console.log("Tab changed to:", newValue)
                }
              />
            ) : (
              <>
                <LeftPanel {...leftPanelProps} />
                <RightPanel {...{ jobData, viewType }} />
              </>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              ...footerStyle,
              display:
                viewType === "completed" || viewType === "missed"
                  ? "none"
                  : "flex",
            }}
          >
            <ModalActions
              {...{
                viewType,
                handleDeclineButtonClick,
                handleApproveJob,
                actionButtonLoader,
                handleCancelButtonClick,
                handleRequestPayment,
                isRequestPaymentDisabled: disabledRequestPaymentBtn?.[
                  jobData?.job_id
                ]
                  ? true
                  : false,
                // Product re-approval flow: when the supplier un-checked items
                // the primary action returns the order to the member instead.
                isProduct,
                hasUnavailable: someUnavailable,
                // Return is only allowed for a partial order (some, not all,
                // items unavailable); otherwise the supplier must cancel.
                canReturn,
                // Why the action is refused, so the footer can say it.
                belowMinimumAfterEdit,
                belowMinimumOrder,
                orderMinimum,
                returnJobLoader,
                handleReturnForReapproval: () =>
                  handleReturnProductForReapproval?.(unavailableIds, quantities),
                // Awaiting the member's re-approval — supplier can only cancel.
                awaitingMember,
                // Active product orders advance to the next status instead of
                // requesting payment.
                nextProductStatusLabel,
                advanceStatusLoader,
                handleAdvanceProductStatus: () =>
                  nextProductStatusId &&
                  handleAdvanceProductStatus?.(nextProductStatusId),
                // COMY1-292 — edit mode: footer becomes Cancel Job +
                // "Update & Notify Client" (submits the shared editor form).
                editMode,
                editFormId: EDIT_ORDER_FORM_ID,
                updateOrderLoader,
                editBelowMinimum,
              }}
            />
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default ViewJobModal;
